<template>
    <section class="student-exam">
        <div class="container student-exam__container" :class="{ 'is-embedded': embedded }">
            <StudentSidebar v-if="!embedded" :student="student" />

            <main class="student-exam__main">
                <header class="student-exam__topbar">
                    <div>
                        <span class="student-exam__eyebrow">{{ exam.typeLabel }}</span>
                        <h1>{{ exam.title }}</h1>
                        <p>{{ exam.courseTitle }}<span v-if="exam.chapterTitle"> / {{ exam.chapterTitle }}</span></p>
                    </div>

                    <div class="student-exam__top-actions">
                        <div v-if="showTimer" class="student-exam__timer" :class="{ 'is-warning': remainingSeconds <= 60 }">
                            <i class="ri-timer-line"></i>
                            <span>{{ timerLabel }}</span>
                        </div>

                        <a :href="exam.backUrl || '/student/dashboard'" class="student-exam__back">
                            <i class="ri-arrow-left-line"></i>
                            Back
                        </a>
                    </div>
                </header>

                <section class="student-exam__stats">
                    <article v-for="stat in stats" :key="stat.label">
                        <i :class="stat.icon"></i>
                        <span>{{ stat.label }}</span>
                        <strong>{{ stat.value }}</strong>
                    </article>
                </section>

                <section v-if="exam.session?.isSubmitted" class="student-exam__result">
                    <div class="student-exam__result-hero">
                        <span class="student-exam__result-icon">
                            <i class="ri-thumb-up-fill"></i>
                        </span>
                        <h2>Thank you</h2>
                        <p>You have completed the {{ exam.typeLabel.toLowerCase() }}.</p>
                    </div>

                    <div class="student-exam__result-card">
                        <div class="student-exam__score-panel">
                            <div class="student-exam__score-ring" :style="scoreRingStyle">
                                <div>
                                    <span>You achieved</span>
                                    <strong>{{ scorePercent }}%</strong>
                                    <small>{{ exam.session.obtainedMark }} out of {{ exam.session.totalMark }} marks</small>
                                </div>
                            </div>
                        </div>

                        <div class="student-exam__breakdown">
                            <h3>Here is the breakdown of your results</h3>
                            <dl>
                                <div>
                                    <dt>Total Questions</dt>
                                    <dd>{{ exam.totalQuestions }}</dd>
                                </div>
                                <div>
                                    <dt>Answered</dt>
                                    <dd>{{ exam.session.totalAnswered }}</dd>
                                </div>
                                <div>
                                    <dt>Skipped question</dt>
                                    <dd class="is-warning">{{ exam.session.totalSkipped }}</dd>
                                </div>
                                <div>
                                    <dt>Incorrect answers</dt>
                                    <dd class="is-danger">{{ exam.session.totalIncorrect }}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <a :href="exam.backUrl || '/student/exams'" class="student-exam__result-back">
                        Back To {{ exam.type === 'quiz' ? 'Quizzes' : 'Exams' }}
                    </a>
                </section>

                <section v-else-if="!exam.session?.canSubmit" class="student-exam__notice">
                    <i class="ri-lock-line"></i>
                    <strong>This assessment is not available right now.</strong>
                    <span>Please check the assessment date or attempt limit.</span>
                </section>

                <form v-else class="student-exam__paper" @submit.prevent="submitExam(false)">
                    <div v-if="showTimer" class="student-exam__timebar">
                        <span :style="{ width: `${timeProgress}%` }"></span>
                    </div>

                    <article v-for="question in exam.questions" :key="question.id" class="student-exam__question">
                        <div class="student-exam__question-head">
                            <span>Question {{ question.number }}</span>
                            <small>{{ question.questionType === 'true_false' ? 'True/False' : 'MCQ' }}</small>
                        </div>

                        <h2>{{ question.questionText }}</h2>

                        <a v-if="question.mediaPath && isImageAttachment(question.mediaPath)" :href="question.mediaPath" target="_blank" rel="noopener" class="student-exam__image-attachment">
                            <img :src="question.mediaPath" :alt="`Question ${question.number} attachment`">
                            <span>
                                <i class="ri-zoom-in-line"></i>
                                View full image
                            </span>
                        </a>

                        <a v-else-if="question.mediaPath" :href="question.mediaPath" target="_blank" rel="noopener" class="student-exam__attachment">
                            <i class="ri-file-text-line"></i>
                            Open question file
                        </a>

                        <div class="student-exam__options">
                            <label v-for="option in question.options" :key="`${question.id}-${option.key}`">
                                <input v-model="answers[question.id]" type="radio" :name="`question-${question.id}`" :value="option.key" />
                                <span>{{ option.label }}</span>
                            </label>
                        </div>
                    </article>

                    <div class="student-exam__submitbar">
                        <div>
                            <strong>{{ answeredCount }} / {{ exam.questions.length }}</strong>
                            <span>answered</span>
                        </div>
                        <button type="submit" :disabled="isSubmitting">
                            <i class="ri-send-plane-line"></i>
                            {{ isSubmitting ? 'Submitting...' : `Submit ${exam.typeLabel}` }}
                        </button>
                    </div>
                </form>
            </main>
        </div>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentExamTakingComponent')
    || document.getElementById('employeeInternalExamTakingComponent');

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const embedded = root?.dataset.embedded === 'true';
const exam = reactive(parseJson(root?.dataset.exam, { questions: [], session: {} }));
const answers = reactive({});
const isSubmitting = ref(false);
const remainingSeconds = ref(0);
const autoSubmitStarted = ref(false);
let timer = null;

exam.questions?.forEach((question) => {
    if (question.answer) {
        answers[question.id] = question.answer;
    }
});

const stats = computed(() => [
    { label: 'Questions', value: exam.totalQuestions || 0, icon: 'ri-questionnaire-line' },
    { label: 'Duration', value: exam.durationLabel || 'Flexible', icon: 'ri-time-line' },
    { label: 'Total Mark', value: exam.totalMark || 0, icon: 'ri-medal-line' },
    { label: 'Pass Mark', value: exam.passMarks || 0, icon: 'ri-checkbox-circle-line' },
]);

const answeredCount = computed(() => Object.values(answers).filter(Boolean).length);

const showTimer = computed(() => Boolean(exam.session?.canSubmit && exam.session?.expiresAt && exam.durationSeconds > 0));

const timerLabel = computed(() => {
    const minutes = Math.floor(Math.max(remainingSeconds.value, 0) / 60);
    const seconds = Math.max(remainingSeconds.value, 0) % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

const timeProgress = computed(() => {
    if (!exam.durationSeconds) {
        return 100;
    }

    return Math.max(Math.min((remainingSeconds.value / exam.durationSeconds) * 100, 100), 0);
});

const scorePercent = computed(() => {
    const total = Number(exam.session?.totalMark || 0);

    if (total <= 0) {
        return 0;
    }

    return Math.round((Number(exam.session?.obtainedMark || 0) / total) * 100);
});

const scoreRingStyle = computed(() => ({
    '--score-progress': `${scorePercent.value * 3.6}deg`,
}));

const isImageAttachment = (path) => {
    const cleanPath = String(path || '').split('?')[0].split('#')[0].toLowerCase();

    return ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some((extension) => cleanPath.endsWith(extension));
};

const notify = (message, type = 'success') => {
    if (typeof window.$toast === 'function') {
        window.$toast(message, type);
    }
};

const secondsUntilExpiry = () => {
    if (!exam.session?.expiresAt) {
        return 0;
    }

    return Math.max(Math.ceil((new Date(exam.session.expiresAt).getTime() - Date.now()) / 1000), 0);
};

const stopTimer = () => {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
};

const redirectToList = () => {
    window.location.href = exam.backUrl || '/student/exams';
};

const submitExam = async (redirectAfterSubmit = false) => {
    if (isSubmitting.value || !exam.session?.canSubmit) {
        if (redirectAfterSubmit) {
            redirectToList();
        }

        return;
    }

    isSubmitting.value = true;

    try {
        const payload = {
            session_unique_id: exam.session.uniqueId,
            answers: exam.questions.map((question) => ({
                question_id: question.id,
                answer: answers[question.id] || null,
            })),
        };

        const { data } = await window.axios.post(exam.submitUrl, payload);

        if (data.status) {
            Object.assign(exam.session, data.data.session);
            stopTimer();
            notify(data.data.message || `${exam.typeLabel} submitted successfully.`, data.type || 'success');
            if (redirectAfterSubmit) {
                redirectToList();
                return;
            }

            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    } catch (error) {
        notify(error.response?.data?.message || 'Unable to submit right now.', 'error');
        if (redirectAfterSubmit) {
            redirectToList();
        }
    } finally {
        isSubmitting.value = false;
    }
};

const startTimer = () => {
    if (!showTimer.value) {
        return;
    }

    remainingSeconds.value = secondsUntilExpiry();

    if (remainingSeconds.value <= 0) {
        autoSubmitStarted.value = true;
        submitExam(true);
        return;
    }

    timer = setInterval(() => {
        remainingSeconds.value = secondsUntilExpiry();

        if (remainingSeconds.value <= 0 && !autoSubmitStarted.value) {
            autoSubmitStarted.value = true;
            stopTimer();
            submitExam(true);
        }
    }, 1000);
};

onMounted(startTimer);

onBeforeUnmount(stopTimer);
</script>

<style scoped>
.student-exam {
    background: linear-gradient(180deg, #f5f8fc 0%, #eef3f7 52%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-exam__container.is-embedded {
    grid-template-columns: minmax(0, 1fr);
    max-width: none;
    padding-inline: 0;
}

.student-exam__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-exam__main {
    min-width: 0;
}

.student-exam__topbar,
.student-exam__stats article,
.student-exam__paper,
.student-exam__result,
.student-exam__notice {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-exam__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.06)), #ffffff;
    border-color: #dcecff;
}

.student-exam__topbar h1,
.student-exam__question h2,
.student-exam__result h2 {
    margin: 0;
    color: #111827;
    font-weight: 900;
}

.student-exam__topbar h1 {
    font-size: 24px;
}

.student-exam__top-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.student-exam__topbar p,
.student-exam__result p {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
}

.student-exam__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
}

.student-exam__back,
.student-exam__attachment {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 900;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.student-exam__back:hover,
.student-exam__attachment:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-exam__image-attachment {
    display: block;
    width: min(100%, 520px);
    margin: 16px 0 18px;
    overflow: hidden;
    border: 1px solid #dbeafe;
    border-radius: 8px;
    background: #f8fafc;
    color: #0d6efd;
    text-decoration: none;
}

.student-exam__image-attachment img {
    display: block;
    width: 100%;
    max-height: 320px;
    object-fit: contain;
    background: #ffffff;
}

.student-exam__image-attachment span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 900;
}

.student-exam__timer {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 40px;
    min-width: 96px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #fff7ed;
    color: #ea580c;
    font-size: 14px;
    font-weight: 900;
}

.student-exam__timer.is-warning {
    background: #fef2f2;
    color: #dc2626;
}

.student-exam__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
    margin-bottom: 18px;
}

.student-exam__stats article {
    display: grid;
    gap: 8px;
    padding: 16px;
}

.student-exam__stats i {
    color: #0d6efd;
    font-size: 24px;
}

.student-exam__stats span {
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
}

.student-exam__stats strong {
    color: #111827;
    font-size: 20px;
    font-weight: 900;
}

.student-exam__paper {
    position: relative;
    overflow: hidden;
}

.student-exam__timebar {
    position: sticky;
    top: 0;
    z-index: 2;
    height: 6px;
    background: #fee2e2;
}

.student-exam__timebar span {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, #22c55e 0%, #facc15 58%, #ef4444 100%);
    transition: width 0.35s ease;
}

.student-exam__question {
    padding: 22px;
    border-bottom: 1px solid #eef2f7;
}

.student-exam__question-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.student-exam__question-head span,
.student-exam__question-head small {
    color: #0d6efd;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
}

.student-exam__question-head small {
    color: #64748b;
}

.student-exam__question h2 {
    font-size: 18px;
    line-height: 1.5;
}

.student-exam__attachment {
    min-height: 34px;
    margin-top: 12px;
    padding: 7px 11px;
    font-size: 12px;
}

.student-exam__options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 16px;
}

.student-exam__options label {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 9px;
    align-items: center;
    min-height: 48px;
    padding: 12px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    color: #334155;
    font-size: 14px;
    font-weight: 800;
}

.student-exam__options label:has(input:checked) {
    border-color: #0d6efd;
    background: #e7f1ff;
    color: #0d6efd;
}

.student-exam__submitbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 22px;
}

.student-exam__submitbar strong,
.student-exam__submitbar span {
    display: block;
}

.student-exam__submitbar strong {
    color: #111827;
    font-size: 18px;
    font-weight: 900;
}

.student-exam__submitbar span {
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
}

.student-exam__submitbar button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 42px;
    padding: 10px 16px;
    border: 0;
    border-radius: 8px;
    background: linear-gradient(135deg, #0d6efd 0%, #2f7d5a 100%);
    color: #ffffff;
    font-size: 13px;
    font-weight: 900;
}

.student-exam__submitbar button:disabled {
    opacity: 0.7;
}

.student-exam__result {
    display: grid;
    gap: 22px;
    padding: 28px 32px 32px;
    border-color: #dbe4ef;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
}

.student-exam__result-hero {
    display: grid;
    justify-items: center;
    gap: 8px;
    text-align: center;
}

.student-exam__result-icon {
    display: grid;
    place-items: center;
    width: 58px;
    height: 58px;
    border-radius: 18px;
    background: #dcfce7;
    color: #2fb344;
    font-size: 34px;
}

.student-exam__result-hero h2 {
    margin: 4px 0 0;
    color: #111827;
    font-size: 26px;
    font-weight: 900;
}

.student-exam__result-hero p {
    color: #111827;
    font-size: 15px;
    font-weight: 700;
}

.student-exam__result-card {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) minmax(320px, 0.9fr);
    gap: 20px;
    padding: 12px;
    border-radius: 18px;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.student-exam__score-panel {
    display: grid;
    place-items: center;
    min-height: 252px;
}

.student-exam__score-ring {
    display: grid;
    place-items: center;
    width: 166px;
    height: 166px;
    border-radius: 50%;
    background: conic-gradient(#0d6efd 0 var(--score-progress), #bfdbfe var(--score-progress) 360deg);
}

.student-exam__score-ring > div {
    display: grid;
    place-items: center;
    width: 132px;
    height: 132px;
    box-sizing: border-box;
    padding: 14px;
    border-radius: 50%;
    background: #f8fbff;
    text-align: center;
}

.student-exam__score-ring span,
.student-exam__score-ring small {
    color: #111827;
    font-size: 12px;
    font-weight: 700;
    max-width: 94px;
    line-height: 1.25;
}

.student-exam__score-ring strong {
    color: #111827;
    font-size: 28px;
    font-weight: 900;
    line-height: 1;
    max-width: 100%;
    overflow-wrap: anywhere;
}

.student-exam__breakdown {
    overflow: hidden;
    border-radius: 14px;
    background: #ffffff;
}

.student-exam__breakdown h3 {
    margin: 0;
    padding: 18px 22px;
    color: #111827;
    font-size: 16px;
    font-weight: 900;
}

.student-exam__breakdown dl {
    margin: 0;
}

.student-exam__breakdown div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 22px;
    border-top: 1px solid #eef2f7;
}

.student-exam__breakdown dt,
.student-exam__breakdown dd {
    margin: 0;
}

.student-exam__breakdown dt {
    color: #64748b;
    font-size: 15px;
    font-weight: 700;
}

.student-exam__breakdown dd {
    color: #111827;
    font-size: 16px;
    font-weight: 900;
}

.student-exam__breakdown dd.is-warning {
    color: #2563eb;
}

.student-exam__breakdown dd.is-danger {
    color: #ef4444;
}

.student-exam__result-back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 56px;
    width: 100%;
    border-radius: 999px;
    background: #0d6efd;
    color: #ffffff;
    font-size: 15px;
    font-weight: 900;
    text-decoration: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.student-exam__result-back:hover {
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(13, 110, 253, 0.24);
}

.student-exam__notice {
    display: grid;
    justify-items: center;
    gap: 8px;
    padding: 42px 16px;
    color: #64748b;
    text-align: center;
}

.student-exam__notice i {
    color: #0d6efd;
    font-size: 34px;
}

.student-exam__notice strong {
    color: #111827;
    font-size: 16px;
    font-weight: 900;
}

@media (max-width: 991px) {
    .student-exam__container {
        grid-template-columns: 1fr;
    }

    .student-exam__stats,
    .student-exam__options {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .student-exam__result-card {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 575px) {
    .student-exam__container {
        padding: 0 10px;
    }

    .student-exam__topbar,
    .student-exam__submitbar {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-exam__top-actions {
        width: 100%;
        flex-direction: column;
        align-items: stretch;
    }

    .student-exam__stats,
    .student-exam__options {
        grid-template-columns: 1fr;
    }

    .student-exam__back,
    .student-exam__submitbar button {
        width: 100%;
    }

    .student-exam__result {
        padding: 22px 16px;
    }

    .student-exam__result-card {
        padding: 10px;
    }
}
</style>
